import * as React from "react";
import { IoAnalyticsOutline } from "react-icons/io5";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { IAdvisorComparisonTableBlock } from "@types";

import { cn } from "../cn";
import { SURFACE } from "../theme";
import {
  abbreviate,
  buildChart,
  ChartTooltip,
  DistributionView,
  isCountColumn,
  isMedianColumn,
  ViewToggle,
} from "./chartCommon";
import {
  alignFromNumeric,
  DataTable,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from "./DataTable";
import { SeriesDot } from "./series";
import { numericColumns } from "./tableUtils";
import { ToggledView } from "./ToggledView";

/**
 * S1B_INTERNAL's peer distribution, rendered two ways: a table with a coloured marker per row
 * by default, or a line chart of the same numbers, switched by the toggle above it. The
 * sample-count ("n") column is dropped from both views.
 */

function TableView({ block }: { block: IAdvisorComparisonTableBlock }) {
  const numeric = numericColumns(block);
  const align = alignFromNumeric(numeric);
  const visible = block.columns
    .map((_, i) => i)
    .filter(i => !isCountColumn(block.columns[i]));

  return (
    <DataTable>
      <DataTableHead
        columns={visible.map(i => block.columns[i])}
        align={visible.map(i => align[i])}
      />
      <tbody>
        {block.rows.map((row, r) => (
          <DataTableRow key={r} sentiment={row.sentiment}>
            {visible.map(c => {
              const cell = row.cells[c] ?? "";
              if (c === 0) {
                return (
                  <DataTableCell key={c} className="font-medium">
                    <span className="flex items-center gap-2.5">
                      <SeriesDot index={r} />
                      {cell}
                    </span>
                  </DataTableCell>
                );
              }
              // The median is the headline of a distribution, so it carries the weight; the
              // rest of the spread sits back in muted text.
              const median = isMedianColumn(block.columns[c] ?? "");
              return (
                <DataTableCell
                  key={c}
                  align={align[c]}
                  className={cn(median ? "font-semibold" : "text-gray-500")}
                >
                  {cell}
                </DataTableCell>
              );
            })}
          </DataTableRow>
        ))}
      </tbody>
    </DataTable>
  );
}

function LineChartView({ block }: { block: IAdvisorComparisonTableBlock }) {
  const { series, data } = buildChart(block);

  return (
    <div className={cn(SURFACE, "px-1 pb-2 pt-3")}>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={data}
          margin={{ top: 8, right: 20, bottom: 4, left: 4 }}
        >
          <CartesianGrid vertical={false} stroke="#E5E7EB" />
          <XAxis
            dataKey="axis"
            tickLine={false}
            axisLine={false}
            dy={8}
            tick={{ fontSize: 12, fill: "#6B7280" }}
          />
          <YAxis
            width={48}
            tickLine={false}
            axisLine={false}
            tickFormatter={abbreviate}
            tick={{ fontSize: 12, fill: "#6B7280" }}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#E5E7EB" }} />
          {series.map(s => (
            <Line
              key={s.label}
              type="monotone"
              dataKey={s.label}
              stroke={s.stroke}
              strokeWidth={2}
              dot={{ r: 3.5, fill: s.stroke, strokeWidth: 0 }}
              activeDot={{ r: 5 }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DistributionTable({
  block,
}: {
  block: IAdvisorComparisonTableBlock;
}) {
  const [view, setView] = React.useState<DistributionView>("table");

  return (
    <ToggledView
      toggle={
        <ViewToggle
          view={view}
          onChange={setView}
          chartIcon={<IoAnalyticsOutline className="h-4 w-4" />}
        />
      }
    >
      {view === "table" ? (
        <TableView block={block} />
      ) : (
        <LineChartView block={block} />
      )}
    </ToggledView>
  );
}
